import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Folder as FolderIcon,
  Download as DownloadIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { usePreferencesStore } from '../../store/preferencesStore';
import { loadRaspiData } from '../../services/api';


interface LoadRaspiDataDialogProps {
  open: boolean;
  onClose: () => void;
  raspiId: string;
}

interface TransferStatus {
  status: 'IDLE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress: number;
  currentFile?: string;
  filesProcessed: number;
  totalFiles: number;
  bytesTransferred: number;
  totalBytes: number;
  errorMessage?: string;
}

export const LoadRaspiDataDialog: React.FC<LoadRaspiDataDialogProps> = ({
  open,
  onClose,
  raspiId
}) => {
  // Remove destinationPath and local folder selection
  const [transferStatus, setTransferStatus] = useState<TransferStatus>({
    status: 'IDLE',
    progress: 0,
    filesProcessed: 0,
    totalFiles: 0,
    bytesTransferred: 0,
    totalBytes: 0
  });
  const [isTransferring, setIsTransferring] = useState(false);
  const { STORE_PATH } = usePreferencesStore();
  const [raspiDataFolder, setRaspiDataFolder] = useState('/home/iop/IOP_DATA');

  useEffect(() => {
    if (open) {
      setRaspiDataFolder(STORE_PATH || '/home/iop/IOP_DATA');
      setTransferStatus({
        status: 'IDLE',
        progress: 0,
        filesProcessed: 0,
        totalFiles: 0,
        bytesTransferred: 0,
        totalBytes: 0
      });
    }
  }, [open, raspiId, STORE_PATH]);

  const handleStartTransfer = async () => {
    setIsTransferring(true);
    setTransferStatus({
      status: 'IN_PROGRESS',
      progress: 0,
      filesProcessed: 0,
      totalFiles: 0,
      bytesTransferred: 0,
      totalBytes: 0
    });
    try {
      await loadRaspiData(raspiId);
      setTransferStatus(prev => ({
        ...prev,
        status: 'COMPLETED',
        progress: 100
      }));
    } catch (error) {
      setTransferStatus(prev => ({
        ...prev,
        status: 'FAILED',
        errorMessage: error instanceof Error ? error.message : 'Transfer failed'
      }));
    } finally {
      setIsTransferring(false);
    }
  };



  const getStatusColor = (status: TransferStatus['status']) => {
    switch (status) {
      case 'COMPLETED': return 'success.main';
      case 'FAILED': return 'error.main';
      case 'IN_PROGRESS': return 'info.main';
      default: return 'text.secondary';
    }
  };

  const getStatusText = (status: TransferStatus['status']) => {
    switch (status) {
      case 'IDLE': return 'Ready to transfer';
      case 'IN_PROGRESS': return 'Transferring files...';
      case 'COMPLETED': return 'Transfer completed successfully';
      case 'FAILED': return 'Transfer failed';
      default: return 'Unknown status';
    }
  };

  return (
    <Dialog open={open} onClose={isTransferring ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DownloadIcon color="primary" />
          <Typography variant="h6">Load RASPI Files</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            ({raspiId})
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Source Path (RASPI) */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Source Path (RASPI)
            </Typography>
            <TextField
              fullWidth
              value={raspiDataFolder}
              disabled
              InputProps={{
                startAdornment: <FolderIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Folder on RASPI device to copy from
            </Typography>
          </Box>
          <Divider />
          {/* Transfer Progress */}
          {transferStatus.status !== 'IDLE' && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color={getStatusColor(transferStatus.status)}>
                  {getStatusText(transferStatus.status)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {transferStatus.progress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={transferStatus.progress}
                color={transferStatus.status === 'FAILED' ? 'error' : 'primary'}
                sx={{ mb: 2 }}
              />
              {transferStatus.errorMessage && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {transferStatus.errorMessage}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {transferStatus.status === 'COMPLETED' ? (
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              component="a"
              href={`/download/raspi_data?raspi_id=${encodeURIComponent(raspiId)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Files
            </Button>
            <Button onClick={onClose} variant="outlined">Close</Button>
          </>
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={isTransferring}
              startIcon={<CancelIcon />}
            >
              {isTransferring ? 'Cancel' : 'Close'}
            </Button>
            <Button
              onClick={handleStartTransfer}
              disabled={isTransferring}
              variant="contained"
              startIcon={<DownloadIcon />}
            >
              {isTransferring ? 'Transferring...' : 'Start Transfer'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LoadRaspiDataDialog; 